import React, { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Code, Copy, Database, ExternalLink, MessageSquare, Network, RefreshCw, Settings, Terminal, Trash2, Zap } from 'lucide-react'
import type { Service, ToolFunction, ProbeResult, LogEntry } from '../types'

interface ServiceDetailProps {
  service: Service
  onProbe: (url: string) => void
  onShowLogs: () => void
  onDelete?: (service: Service) => void
  deleting?: boolean
}

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  read: Database,
  write: Zap,
  exec: Terminal,
  admin: Settings,
  chat: MessageSquare,
  mesh: Network,
}

const CATEGORY_COLORS: Record<string, string> = {
  read: 'text-blue-400 border-blue-400/30',
  write: 'text-green-400 border-green-400/30',
  exec: 'text-orange-400 border-orange-400/30',
  admin: 'text-purple-400 border-purple-400/30',
  chat: 'text-pink-400 border-pink-400/30',
  mesh: 'text-cyan-400 border-cyan-400/30',
}

function StatusBadge({ status }: { status: string }) {
  const isUp = status === 'running' || status === 'up' || status === 'healthy' || status === 'OK'
  const isGated = status === 'gated' || status === 'access-gated' || status === 'AUTH-GATED' || status === '401'
  const isDown = status === 'down' || status === 'error' || status === 'DOWN' || status === 'failed' || status.includes('timeout') || status.includes('refused')
  
  if (isUp) return <span className="px-2 py-0.5 text-[10px] font-mono bg-green-500/20 text-green-400 border border-green-400/30 rounded">UP</span>
  if (isGated) return <span className="px-2 py-0.5 text-[10px] font-mono bg-blue-500/20 text-blue-400 border border-blue-400/30 rounded">GATED</span>
  if (isDown) return <span className="px-2 py-0.5 text-[10px] font-mono bg-red-500/20 text-red-400 border border-red-400/30 rounded">DOWN</span>
  return <span className="px-2 py-0.5 text-[10px] font-mono bg-yellow-500/20 text-yellow-400 border border-yellow-400/30 rounded">{status.toUpperCase()}</span>
}

function ToolCard({ tool, serviceUrl }: { tool: ToolFunction; serviceUrl: string }) {
  const Icon = CATEGORY_ICONS[tool.category] || Settings
  const colorClass = CATEGORY_COLORS[tool.category] || 'text-zinc-400 border-zinc-400/30'
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  const copyCurl = () => {
    const curl = `curl -X ${tool.method} "${serviceUrl.replace(/\/$/, '')}${tool.path}"`
    navigator.clipboard.writeText(curl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={`glass-panel rounded-lg border-l-4 ${colorClass} overflow-hidden`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded bg-white/5 ${colorClass}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-mono text-sm font-medium text-white">{tool.method}</h4>
              <code className="text-xs bg-white/5 px-2 py-0.5 rounded font-mono text-cyan-300">{tool.path}</code>
              <span className={`px-1.5 py-0.5 text-[9px] font-mono rounded ${colorClass.replace('text-', 'bg-').replace('border-', 'bg-').replace('/30', '/20')} text-white`}>{tool.category.toUpperCase()}</span>
            </div>
            <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{tool.description}</p>
            {tool.parameters && (
              <details className="mt-2">
                <summary className="text-[10px] text-zinc-500 cursor-pointer hover:text-zinc-300">Parameters</summary>
                <pre className="mt-1 text-[10px] text-zinc-300 bg-black/30 p-2 rounded overflow-x-auto font-mono">{JSON.stringify(tool.parameters, null, 2)}</pre>
              </details>
            )}
          </div>
        </div>
        
        <div className="mt-3 flex items-center gap-2 pt-3 border-t border-white/5">
          <button
            onClick={copyCurl}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono bg-white/5 hover:bg-white/10 rounded border border-white/10 transition-colors"
            title="Copy cURL"
          >
            <Copy className="w-3 h-3" />
            <span>{copied ? 'COPIED' : 'COPY cURL'}</span>
          </button>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono bg-white/5 hover:bg-white/10 rounded border border-white/10 transition-colors"
            title="Test in new tab"
          >
            <ExternalLink className="w-3 h-3" />
            TEST
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono bg-white/5 hover:bg-white/10 rounded border border-white/10 transition-colors"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {expanded ? 'HIDE' : 'DETAILS'}
          </button>
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-white/5 space-y-2 text-[10px] font-mono text-zinc-300">
            <div className="flex items-center gap-2 text-zinc-500">
              <span className="w-16">FULL URL</span>
              <code className="break-all">{serviceUrl.replace(/\/$/, '')}{tool.path}</code>
            </div>
            {tool.returns && (
              <div className="flex items-center gap-2 text-zinc-500">
                <span className="w-16">RETURNS</span>
                <code>{tool.returns}</code>
              </div>
            )}
            <div className="flex items-center gap-2 text-zinc-500">
              <span className="w-16">CATEGORY</span>
              <span className={colorClass}>{tool.category}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function ServiceDetail({ service, onProbe, onShowLogs, onDelete, deleting }: ServiceDetailProps) {
  const [tools, setTools] = useState<ToolFunction[]>([])
  const [loadingTools, setLoadingTools] = useState(false)
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    async function discoverTools() {
      if (!service.tools || service.tools.length === 0) {
        setLoadingTools(true)
        try {
          // Try to fetch OpenAPI spec from service
          const baseUrl = service.url.replace(/\/$/, '')
          const endpoints = ['/openapi.json', '/docs/openapi.json', '/api/openapi.json', '/swagger.json', '/api/docs']
          
          for (const ep of endpoints) {
            try {
              const res = await fetch(`${baseUrl}${ep}`, { cache: 'no-store' })
              if (res.ok) {
                const spec = await res.json()
                const discovered: ToolFunction[] = []
                
                if (spec.paths) {
                  for (const [path, methods] of Object.entries(spec.paths)) {
                    if (methods && typeof methods === 'object') {
                      for (const [method, details] of Object.entries(methods as any)) {
                        if (['get', 'post', 'put', 'delete', 'patch'].includes(method.toLowerCase())) {
                          const d = details as any
                          discovered.push({
                            name: d.operationId || `${method.toUpperCase()} ${path}`,
                            description: d.summary || d.description || 'No description',
                            method: method.toUpperCase() as ToolFunction['method'],
                            path,
                            parameters: d.parameters ? d.parameters.reduce((acc: any, p: any) => ({ ...acc, [p.name]: p.schema || p.type || 'any' }), {}) : undefined,
                            returns: d.responses?.['200']?.content?.['application/json']?.schema ? 'JSON' : 'Unknown',
                            category: categorizeTool(path, method, d.summary || ''),
                          })
                        }
                      }
                    }
                  }
                }
                
                if (discovered.length > 0) {
                  setTools(discovered)
                  break
                }
              }
            } catch {}
          }
        } catch (e) {
          console.warn('Tool discovery failed:', e)
        } finally {
          setLoadingTools(false)
        }
      } else {
        setTools(service.tools || [])
      }
    }

    discoverTools()
  }, [service])

  const categorizeTool = (path: string, method: string, summary: string): ToolFunction['category'] => {
    const p = path.toLowerCase()
    const s = summary.toLowerCase()
    const m = method.toLowerCase()
    
    if (p.includes('/chat') || p.includes('/message') || s.includes('chat')) return 'chat'
    if (p.includes('/mesh') || p.includes('/peer') || s.includes('mesh')) return 'mesh'
    if (m !== 'get') return 'write'
    if (p.includes('/health') || p.includes('/status') || p.includes('/info') || p.includes('/list') || p.includes('/get')) return 'read'
    if (p.includes('/admin') || p.includes('/config') || p.includes('/settings')) return 'admin'
    if (p.includes('/exec') || p.includes('/run') || p.includes('/deploy') || p.includes('/build')) return 'exec'
    return 'read'
  }

  const filteredTools = tools.filter(t => {
    const matchesCategory = filterCategory === 'all' || t.category === filterCategory
    const matchesSearch = searchQuery === '' || 
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const categories = ['all', 'read', 'write', 'exec', 'admin', 'chat', 'mesh'] as const

  return (
    <div className="space-y-4">
      {/* Service Header */}
      <div className="glass-panel rounded-lg p-4">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
            <Terminal className="w-6 h-6 text-cyan-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-mono font-medium text-white">{service.name}</h3>
              <StatusBadge status={service.status} />
              <span className="text-[10px] text-zinc-500 px-2 py-0.5 bg-white/5 rounded">{service.host}:{service.port}</span>
            </div>
            <div className="mt-1 flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
              <span className="flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {service.url}</span>
              <span className="flex items-center gap-1"><Database className="w-3 h-3" /> {service.health_endpoint}</span>
              {service.apiSpec && <span className="flex items-center gap-1"><Code className="w-3 h-3" /> OpenAPI v{service.apiSpec.version}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {service.url.startsWith('http://') && (
              <a
                href={service.metadata?.lan_url || service.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-[10px] font-mono bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 rounded hover:bg-cyan-500/20 transition-colors flex items-center gap-1.5"
              >
                <ExternalLink className="w-3 h-3" />
                OPEN ON LAN
              </a>
            )}
            <button
              onClick={() => onProbe(service.health_endpoint || service.url)}
              className="px-3 py-1.5 text-[10px] font-mono bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded hover:bg-cyan-500/30 transition-colors flex items-center gap-1.5"
            >
              <RefreshCw className="w-3 h-3" />
              PROBE
            </button>
            <button
              onClick={onShowLogs}
              className="px-3 py-1.5 text-[10px] font-mono bg-white/5 text-zinc-300 border border-white/10 rounded hover:bg-white/10 transition-colors flex items-center gap-1.5"
            >
              <MessageSquare className="w-3 h-3" />
              LOGS
            </button>
            {/^forge-job-\d+$/.test(service.name) && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(service)}
                disabled={deleting}
                className="px-3 py-1.5 text-[10px] font-mono bg-rose-500/15 text-rose-300 border border-rose-400/40 rounded hover:bg-rose-500/25 transition-colors flex items-center gap-1.5 disabled:opacity-40"
              >
                <Trash2 className="w-3 h-3" />
                {deleting ? 'DELETING' : 'DELETE JOB'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tools Section */}
      <div className="glass-panel rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[10px] tracking-[0.15em] text-zinc-500 uppercase">AI TOOLS & FUNCTIONS</div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-1.5 text-[11px] font-mono bg-white/5 border border-white/10 rounded w-48 placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/50"
            />
            <div className="flex items-center gap-1 bg-white/5 rounded border border-white/10 p-0.5">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilterCategory(cat)}
                  className={`px-2.5 py-1 text-[9px] font-mono rounded transition-colors ${
                    filterCategory === cat
                      ? 'bg-cyan-500/30 text-cyan-300'
                      : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  {cat.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4">
          {loadingTools ? (
            <div className="flex items-center justify-center py-12 text-zinc-500">
              <RefreshCw className="w-6 h-6 animate-spin text-cyan-400 mr-2" />
              Discovering tools from OpenAPI spec...
            </div>
          ) : filteredTools.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Settings className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No tools discovered</p>
              <p className="text-[11px] mt-1">Service may not expose OpenAPI spec or tools endpoint</p>
              <button
                onClick={() => {
                  setLoadingTools(true)
                  setTimeout(() => setLoadingTools(false), 100)
                }}
                className="mt-3 px-3 py-1.5 text-[10px] font-mono bg-white/5 border border-white/10 rounded hover:bg-white/10 transition-colors"
              >
                RETRY DISCOVERY
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredTools.map(tool => (
                <ToolCard key={`${tool.method}-${tool.path}`} tool={tool} serviceUrl={service.url} />
              ))}
            </div>
          )}
          
          {filteredTools.length > 0 && (
            <div className="mt-4 text-right text-[10px] text-zinc-500">
              {filteredTools.length} of {tools.length} tools visible
            </div>
          )}
        </div>
      </div>

      {/* Service Metadata */}
      <div className="glass-panel rounded-lg p-4">
        <div className="text-[10px] tracking-[0.15em] text-zinc-500 uppercase mb-3">SERVICE METADATA</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-zinc-500">Host:</span> <span className="font-mono text-cyan-300 ml-2">{service.host}</span></div>
          <div><span className="text-zinc-500">Port:</span> <span className="font-mono text-cyan-300 ml-2">{service.port}</span></div>
          <div><span className="text-zinc-500">Last Check:</span> <span className="font-mono text-cyan-300 ml-2">{new Date(service.last_check).toLocaleString()}</span></div>
          <div><span className="text-zinc-500">Health EP:</span> <span className="font-mono text-cyan-300 ml-2 truncate">{service.health_endpoint}</span></div>
        </div>
      </div>
    </div>
  )
}